import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  ListItemIcon,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import {
  DataGrid,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import CEQuickFilter from "components/CEQuickFilter";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import Grid from "@mui/material/Grid2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { EditRounded } from "@mui/icons-material";
import OpenInBrowserRoundedIcon from "@mui/icons-material/OpenInBrowserRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import Dialog from "components/Dialog";
import api from "services/api";
import dayjs from "dayjs";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";

const BugReports = () => {
  usePageTitle("Bug Reports");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.Role === "Admin";

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [listOfBugReports, setListOfBugReports] = useState([]);

  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  const { setOverlayData, setOverlayName } = useStateContext();
  const { refreshPage } = useStateContext();

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleContextMenuOpen = () => {
    const data = {
      BugReport: listOfBugReports.find((bug) => bug.id === selectedRow),
      BugReportID: selectedRow,
    };

    setOverlayData(data);

    navigate(`/bugReports/${selectedRow}`, { state: data });

    setContextMenu(null);
  };

  const handleContextMenuEdit = () => {
    const data = {
      BugReportID: selectedRow,
      BugReport: listOfBugReports.find((bug) => bug.id === selectedRow),
      Action: OVERLAY_NAMES.BUG_REPORT_ACTION_EDIT,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.BUG_REPORT_NEW);

    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        captureGridScroll();
        const response = await api.get("/bugReports");
        if (response && response.data) {
          setListOfBugReports(response.data);
          restoreGridScroll(response.data);
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    handleErrorDialog,
    refreshPage,
    theme,
    captureGridScroll,
    restoreGridScroll,
  ]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const columns = [
    {
      field: "Title",
      headerName: "Title",
      minWidth: 200,
      flex: 2.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Reporter.Name",
      headerName: "Reported By",
      valueGetter: (_, params) => {
        return params.Reporter?.Name || "N/A";
      },
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Priority",
      headerName: "Priority",
      minWidth: 100,
      flex: 1.0,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Category",
      headerName: "Category",
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Assignee.Name",
      headerName: "Assigned To",
      valueGetter: (_, params) => {
        return params.Assignee?.Name || "N/A";
      },
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "createdAt",
      headerName: "Reported Date",
      valueGetter: (_, params) => {
        const data = params.createdAt;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Description",
      headerName: "Description",
      minWidth: 300,
      flex: 3.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[7] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfBugReports || [], columns);

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Title", valueGetter: (row) => row.Title || "N/A" },
      {
        field: "Reporter.Name",
        valueGetter: (row) => row.Reporter?.Name || "N/A",
      },
      { field: "Status", valueGetter: (row) => row.Status || "N/A" },
      { field: "Priority", valueGetter: (row) => row.Priority || "N/A" },
      { field: "Category", valueGetter: (row) => row.Category || "N/A" },
      {
        field: "Assignee.Name",
        valueGetter: (row) => row.Assignee?.Name || "N/A",
      },
      {
        field: "createdAt",
        valueGetter: (row) =>
          row.createdAt ? dayjs.tz(row.createdAt).format("M/D/YYYY") : "N/A",
      },
      { field: "Description", valueGetter: (row) => row.Description || "N/A" },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listOfBugReports || [];
      }

      return (listOfBugReports || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listOfBugReports, filterColumns]);

  // Action handlers
  const handleDeleteOne = (id) => {
    const report = listOfBugReports.find((b) => b.id === id);
    openDialog({
      theme,
      title: "Delete Bug Report",
      message: `Delete "${report?.Title || id}"?`,
      customContent: null,
      secondaryAction: "Cancel",
      primaryAction: "Delete",
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: async () => {
        try {
          await api.post("/bugReports/deleteBugReport", { bugReportId: id });
          setListOfBugReports((prev) => prev.filter((r) => r.id !== id));
          setSelectionModel([]);
          setSelectionEnabled(false);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Unable to delete bug report. Please try again later.";
          handleErrorDialog(message, theme);
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handleDeleteMany = (ids) => {
    openDialog({
      theme,
      title: "Delete Selected",
      message: `Delete ${ids.length} bug report(s)?`,
      customContent: null,
      secondaryAction: "Cancel",
      primaryAction: "Delete All",
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: async () => {
        try {
          await Promise.all(
            ids.map((id) =>
              api.post("/bugReports/deleteBugReport", { bugReportId: id }),
            ),
          );
          setListOfBugReports((prev) =>
            prev.filter((r) => !ids.includes(r.id)),
          );
          setSelectionModel([]);
          setSelectionEnabled(false);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Unable to delete selected bug reports. Please try again later.";
          handleErrorDialog(message, theme);
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handleResolveOne = (id) => {
    const report = listOfBugReports.find((b) => b.id === id);
    openDialog({
      theme,
      title: "Resolve Bug Report",
      message: `Resolve "${report?.Title || id}"?`,
      customContent: (
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Resolution Notes"
          placeholder="Add resolution notes (optional)"
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      ),
      secondaryAction: "Cancel",
      primaryAction: "Resolve",
      secondaryOnClick: () => {
        setResolutionNotes("");
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/bugReports/resolveBugReport", {
            bugReportId: id,
            resolvedBy: currentUser.id,
            resolutionNotes,
          });
          setListOfBugReports((prev) =>
            prev.map((r) => (r.id === id ? { ...r, Status: "Resolved" } : r)),
          );
          setSelectionModel([]);
          setSelectionEnabled(false);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Unable to resolve bug report. Please try again later.";
          handleErrorDialog(message, theme);
        } finally {
          setResolutionNotes("");
          closeDialog();
        }
      },
    });
  };

  const handleResolveMany = (ids) => {
    openDialog({
      theme,
      title: "Resolve Selected",
      message: `Resolve ${ids.length} bug report(s)?`,
      customContent: (
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Resolution Notes"
          placeholder="Apply notes to all selected (optional)"
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      ),
      secondaryAction: "Cancel",
      primaryAction: "Resolve All",
      secondaryOnClick: () => {
        setResolutionNotes("");
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await Promise.all(
            ids.map((id) =>
              api.post("/bugReports/resolveBugReport", {
                bugReportId: id,
                resolvedBy: currentUser.id,
                resolutionNotes,
              }),
            ),
          );
          setListOfBugReports((prev) =>
            prev.map((r) =>
              ids.includes(r.id) ? { ...r, Status: "Resolved" } : r,
            ),
          );
          setSelectionModel([]);
          setSelectionEnabled(false);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Unable to resolve selected bug reports. Please try again later.";
          handleErrorDialog(message, theme);
        } finally {
          setResolutionNotes("");
          closeDialog();
        }
      },
    });
  };

  const handleDelete = () => {
    if (selectionModel.length === 1) return handleDeleteOne(selectionModel[0]);
    if (selectionModel.length > 1) return handleDeleteMany(selectionModel);
  };

  const handleResolve = () => {
    if (selectionModel.length === 1) return handleResolveOne(selectionModel[0]);
    if (selectionModel.length > 1) return handleResolveMany(selectionModel);
  };

  const CustomToolbar = () => {
    const deleteLabel = selectionModel.length > 1 ? "Delete All" : "Delete";
    const resolveLabel = selectionModel.length > 1 ? "Resolve All" : "Resolve";

    return (
      <GridToolbarContainer
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 0,
        }}
      >
        {selectionEnabled && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectionModel([]);
                setSelectionEnabled(false);
              }}
              sx={{
                mx: 1,
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              Cancel Selection
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                selectionModel.length > 1 ? (
                  <DeleteSweepRoundedIcon />
                ) : (
                  <DeleteRoundedIcon />
                )
              }
              disabled={selectionModel.length === 0}
              onClick={handleDelete}
              sx={{
                mr: 1,
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              {deleteLabel}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                selectionModel.length > 1 ? (
                  <TaskAltRoundedIcon />
                ) : (
                  <CheckCircleOutlineRoundedIcon />
                )
              }
              disabled={selectionModel.length === 0}
              onClick={handleResolve}
              sx={{
                mr: 1,
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              {resolveLabel}
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {hasActiveFilters && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearAllFilters}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.buttonHoverSelected,
                backgroundColor: theme.palette.primary.buttonHoverSelected,
                color: theme.palette.background.default,
              },
              mr: 2,
            }}
          >
            Clear Filters
          </Button>
        )}
        <CEQuickFilter />
      </GridToolbarContainer>
    );
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Dialog />

      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ pr: 1 }}
          >
            {isAdmin && (
              <Button
                startIcon={<TerminalRoundedIcon />}
                onClick={() => {
                  setOverlayData({});
                  setOverlayName(OVERLAY_NAMES.LOGS_VIEWER);
                }}
                sx={{
                  mr: 1,
                  width: "200px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    marginRight: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  View Logs
                </Box>
              </Button>
            )}
            <IconButton
              onClick={() => {
                setOverlayData({
                  Action: OVERLAY_NAMES.BUG_REPORT_ACTION_NEW,
                });
                setOverlayName(OVERLAY_NAMES.BUG_REPORT_NEW);
              }}
              sx={{
                padding: "8px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                boxShadow:
                  "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.background.default,
                  },
                },
              }}
            >
              <AddRoundedIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          boxShadow: theme.shadows[3],
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          apiRef={apiRef}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          disableColumnSorting
          disableColumnMenu
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(newSelection)
          }
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }
            if (selectionEnabled) {
              // In selection mode, suppress the first click after long-press to avoid deselection
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
              }
              // Do not navigate while selecting rows
              return;
            }

            const data = {
              BugReport: params.row,
              BugReportID: params.row.id,
            };

            navigate(`/bugReports/${params.row.id}`, {
              state: data,
            });
          }}
          getCellClassName={(params) => {
            return params.value === "N/A" ? "notAvailable" : "";
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuOpen}>
            <ListItemIcon>
              <OpenInBrowserRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Open</Typography>
          </MenuItem>
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default BugReports;
